/**
 * Google Analytics 훅
 * @description GA4 초기화 및 페이지뷰 자동 추적
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { initGA, trackPageView, setUserId, isGAInitialized } from '@/common/utils/analytics';
import { useAuthStore } from '@/store/useAuthStore';

/** GA4 Measurement ID (각 앱별로 다른 데이터 스트림 사용) */
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string;

/** 개발 환경 여부 */
const isDevelopment = import.meta.env.VITE_NODE_ENV === 'development';

/**
 * Google Analytics 훅
 * - GA 초기화 (최초 1회)
 * - 페이지뷰 자동 추적
 * - 로그인 사용자 ID 설정
 * 
 * @example
 * ```tsx
 * const AppContent = () => {
 *   useGoogleAnalytics();
 *   return <Routes>...</Routes>;
 * };
 * ```
 */
export const useGoogleAnalytics = (): void => {
  const location = useLocation();
  const { user } = useAuthStore();

  // GA 초기화 (최초 1회)
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      if (isDevelopment) {
        console.log('[GA] VITE_GA_MEASUREMENT_ID가 설정되지 않았습니다.');
      }
      return;
    }

    if (!isGAInitialized()) {
      initGA(GA_MEASUREMENT_ID);
    }
  }, []);

  // 페이지뷰 추적
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !isGAInitialized()) return;

    // 페이지뷰 추적 (딜레이를 줘서 페이지 타이틀이 업데이트된 후 추적)
    const timeoutId = setTimeout(() => {
      trackPageView(location.pathname + location.search);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  // 로그인 사용자 ID 설정
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !isGAInitialized()) return;

    if (user?.id) {
      setUserId(String(user.id));
    }
  }, [user?.id]);
};

export default useGoogleAnalytics;

